import { Injectable } from '@angular/core';

import * as _ from 'lodash';

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { AddressUtilsService } from 'src/app/shared/services/address-utils.service';
import { environment } from 'src/environments/environment';
import type {
  SubscriptionEstimateItem,
  SubscriptionUpdateItem,
} from '../../billing/services/subscription';
import {
  fromServerToSubscription,
  fromServerToSubscriptionLegacy,
} from '../helpers/store-integrations.helper';
import { StoreSubscriptionAPI } from '../store-api';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class BillingApiService
  extends ApiService<any>
  implements StoreSubscriptionAPI
{
  protected APIUrl =
    environment.SPRING_STORE_ENDPOINT_URL + this.getResourceUrl();

  private INVOICE_WRITABLE_FIELDS = ['poNumber'];

  constructor(
    protected httpClient: HttpClient,
    protected companyStateService: CompanyStateService,
    private addressUtils: AddressUtilsService,
  ) {
    super(httpClient);
  }

  getResourceUrl(): string {
    return '/v2';
  }

  getSubscriptions(search, cursor?) {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('cursor', cursor || '')
      .set('count', search.count ? search.count.toString() : '');

    return firstValueFrom(
      this.requestAsObservable(
        '',
        'GET',
        'integrations/subscriptions/list',
        params,
      ).pipe(
        map((response) => {
          const converter =
            environment.CHARGEBEE === 'V2'
              ? fromServerToSubscription
              : fromServerToSubscriptionLegacy;
          response.items = (response.items || []).map(converter);
          return response;
        }),
      ),
    );
  }

  getSubscription(
    subscriptionId: string,
    withScheduledChanges?: boolean,
  ): Promise<any> {
    const params = new HttpParams()
      .set('subscriptionId', subscriptionId)
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('withScheduledChanges', !!withScheduledChanges);

    return firstValueFrom(
      this.requestAsObservable(
        '',
        'GET',
        'integrations/subscription',
        params,
      ).pipe(
        map((response) => {
          const converter =
            environment.CHARGEBEE === 'V2'
              ? fromServerToSubscription
              : fromServerToSubscriptionLegacy;
          response.item = converter(response.item);
          return response;
        }),
      ),
    );
  }

  estimateSubscriptionUpdate(
    items: SubscriptionEstimateItem[],
    couponCode: string,
    endOfTerm: boolean,
  ): Promise<any> {
    const billingAddress = this.addressUtils.copyAddress(
      this.companyStateService.getCopyOfSelectedCompany(),
    );
    const jsonData = {
      billTo: billingAddress,
      shipTo: billingAddress,
      couponCode: couponCode,
      endOfTerm: endOfTerm,
      items,
    };
    const params = new HttpParams().set('jsonData', JSON.stringify(jsonData));

    return firstValueFrom(
      this.requestAsObservable('', 'GET', 'purchase/estimate', params),
    );
  }

  prepareUpdateSubscription(
    items: SubscriptionUpdateItem[],
    couponCode: string,
    endOfTerm: boolean,
  ): Promise<any> {
    const billingAddress = this.addressUtils.copyAddress(
      this.companyStateService.getCopyOfSelectedCompany(),
    );
    const jsonData = {
      billTo: billingAddress,
      shipTo: billingAddress,
      couponCode: couponCode,
      endOfTerm: endOfTerm,
      items,
    };
    const params = new HttpParams().set('jsonData', JSON.stringify(jsonData));

    return firstValueFrom(
      this.requestAsObservable('', 'POST', 'purchase/prepare', params),
    );
  }

  updateSubscription(
    items: SubscriptionUpdateItem[],
    couponCode: string,
    endOfTerm: boolean,
    card: any,
  ): Promise<any> {
    const billingAddress = this.addressUtils.copyAddress(
      this.companyStateService.getCopyOfSelectedCompany(),
    );
    const multiple = items.filter((item) => item.changed).length > 1;
    const jsonData = {
      billTo: billingAddress,
      shipTo: billingAddress,
      couponCode: couponCode,
      endOfTerm: endOfTerm,
      card,
      items: items.map(({ changed, ...item }) => item),
      invoiceImmediately: !multiple,
      useUnbilledCharges: multiple,
    };
    const params = new HttpParams().set('jsonData', JSON.stringify(jsonData));

    return firstValueFrom(
      this.requestAsObservable('', 'POST', 'purchase/put', params),
    );
  }

  changePoNumber(subscriptionId, poNumber) {
    const params = new HttpParams()
      .set('subscriptionId', subscriptionId)
      .set('poNumber', poNumber)
      .set('companyId', this.companyStateService.getSelectedCompanyId());

    return firstValueFrom(
      this.requestAsObservable(
        '',
        'PUT',
        'integrations/subscription/changePoNumber',
        params,
      ),
    );
  }

  changePaymentSource(subscriptionId, paymentSourceId) {
    const params = new HttpParams()
      .set('subscriptionId', subscriptionId)
      .set('paymentSourceId', paymentSourceId)
      .set('companyId', this.companyStateService.getSelectedCompanyId());

    return firstValueFrom(
      this.requestAsObservable(
        '',
        'PUT',
        'integrations/subscription/changePaymentSource',
        params,
      ),
    );
  }

  changePaymentToInvoice(subscriptionId, poNumber) {
    const params = new HttpParams()
      .set('subscriptionId', subscriptionId)
      .set('poNumber', poNumber)
      .set('companyId', this.companyStateService.getSelectedCompanyId());

    return firstValueFrom(
      this.requestAsObservable(
        '',
        'PUT',
        'integrations/subscription/changePaymentToInvoice',
        params,
      ),
    );
  }

  getInvoices(search, cursor?) {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('cursor', cursor || '')
      .set('count', search.count ? search.count.toString() : '');

    return firstValueFrom(
      this.requestAsObservable('', 'GET', 'integrations/invoices/list', params),
    );
  }

  getUnpaidInvoices(search, cursor?) {
    const headers = new HttpHeaders().set('token', search.token);
    const params = new HttpParams()
      .set('companyId', search.companyId)
      .set('cursor', cursor || '')
      .set('count', search.count ? search.count.toString() : '');

    return firstValueFrom(
      this.requestWithHeadersAsObservable(
        '',
        'GET',
        'integrations/invoices/listUnpaid',
        headers,
        params,
      ),
    );
  }

  getInvoice(invoiceId, companyId, token) {
    const headers = new HttpHeaders().set('token', token);
    const params = new HttpParams().set('companyId', companyId);

    return firstValueFrom(
      this.requestWithHeadersAsObservable(
        '',
        'GET',
        `integrations/invoice/${invoiceId}`,
        headers,
        params,
      ),
    );
  }

  updateInvoice(invoice, companyId, token) {
    var fields = _.pick(invoice, this.INVOICE_WRITABLE_FIELDS);
    const headers = new HttpHeaders().set('token', token);
    const params = new HttpParams()
      .set('companyId', companyId)
      .set('invoiceId', invoice.id)
      .set('jsonData', JSON.stringify(fields));

    return firstValueFrom(
      this.requestWithHeadersAsObservable(
        '',
        'PUT',
        'integrations/invoices/put',
        headers,
        params,
      ),
    );
  }

  getInvoicePdf(invoiceId, companyId, token) {
    const headers = new HttpHeaders().set('token', token);
    const params = new HttpParams()
      .set('companyId', companyId)
      .set('invoiceId', invoiceId);

    return firstValueFrom(
      this.requestWithHeadersAsObservable(
        '',
        'GET',
        'integrations/invoices/getPdf',
        headers,
        params,
      ),
    );
  }

  getCreditCards(search, cursor?) {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('cursor', cursor || '')
      .set('count', search.count ? search.count.toString() : '');

    return firstValueFrom(
      this.requestAsObservable('', 'GET', 'integrations/cards/list', params),
    );
  }

  preparePaymentSource(paymentMethodId) {
    const params = new HttpParams()
      .set('paymentMethodId', paymentMethodId)
      .set('companyId', this.companyStateService.getSelectedCompanyId());

    return firstValueFrom(
      this.requestAsObservable(
        '',
        'POST',
        'integrations/paymentSource/prepare',
        params,
      ),
    );
  }

  addPaymentSource(setupIntentId) {
    const params = new HttpParams()
      .set('setupIntentId', setupIntentId)
      .set('companyId', this.companyStateService.getSelectedCompanyId());

    return firstValueFrom(
      this.requestAsObservable(
        '',
        'POST',
        'integrations/paymentSource',
        params,
      ),
    );
  }

  deletePaymentSource(paymentSourceId) {
    const params = new HttpParams()
      .set('paymentSourceId', paymentSourceId)
      .set('companyId', this.companyStateService.getSelectedCompanyId());

    return firstValueFrom(
      this.requestAsObservable(
        '',
        'DELETE',
        'integrations/paymentSource',
        params,
      ),
    );
  }
}
